import { clone } from 'mobx-keystone';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import ReactFlow, { Controls } from 'react-flow-renderer';
import styled from 'styled-components';
import { ActionEdge } from './ActionEdge';
import { ActionNavigator } from './ActionNavigator';
import { ActionDetails } from './ActionDetails';
import { ActionTreeInfo } from './ActionTreeInfo';
import { ActionNode } from './ActionNode';
import { actionSubTreeFocus } from './ActionSubTreeFocus';
import { ActionConnectionLine } from './ActionConnectionLine';
import { lastElement } from '../../../shared/helper/generalHelpers';
import { undoableActionEditorDeselectAction, undoableActionEditorSelectAction } from '../../stores/undo/operation/ActionEditorSelectActionOp';
import { ActionEditorChangeGroup, groupUndoableActionEditorChanges } from '../../stores/undo/operation/ActionEditorSubmitChangesOp';
import { ActionTreeModel, ActionTreeType, getTreeParent } from '../../../shared/action/ActionTreeModel';
import { Adjustment, SlideMenu, State } from "../menu/SlideMenu";
import { CgListTree } from "react-icons/cg";
import { GrInspect } from "react-icons/gr";
import { AiOutlineInteraction, AiOutlineSearch } from "react-icons/ai";
import { UndoRedoSlideMenu } from '../editor/UndoRedoSlideMenu';
import { getEdgeTarget, undoRedoUpdateReactFlowInstance } from '../../stores/undo/operation/actionEditorSupport';
import { UiConstants } from '../../data/UiConstants';
import { globalActionEditorEvents } from './handle/globalActionEditorEvents';
import { ActionSearchMenu } from "./ActionSearchMenu";
import { onScreenActionPositions, hierarchyScale, actionAsNode, exitAsEdge, alignEntriesOnLeft, alignExitsOnRight, configureNewActionInstance, setActionPositionAligned, reverseScaleNumber } from './actionEditorHelpers';
import { actionEditorStore } from '../../stores/ActionEditorStore';
import { sharedStore } from '../../stores/SharedStore';
import { undoableActionEditorCreateSubtrees } from '../../stores/undo/operation/ActionEditorCreateSubtreeOp';
import { runInAction } from 'mobx';
import { localSettingsStore } from '../../stores/LocalSettingsStore';
import { EditorComplexity } from '../../../shared/definitions/other/EditorComplexity';
import { actionModelPrototypes } from './actionNodeData';
const actionDetailsTop = 40;
const navigatorHeight = 260;
const StyledReactFlow = styled.div.withConfig({
  displayName: "ActionEditor__StyledReactFlow",
  componentId: "sc-ckwkcr-0"
})([".react-flow__edges{z-index:", ";filter:drop-shadow(1px 1px 3px rgb(0 0 0 / 20%));}"], UiConstants.Z_INDEX_ACTION_EDITOR_EDGE);
const FullscreenContainer = styled.div.withConfig({
  displayName: "ActionEditor__FullscreenContainer",
  componentId: "sc-ckwkcr-1"
})(["position:fixed;left:0;right:0;top:0;bottom:0;"]);
export const ActionEditor = observer(() => {
  var _zoomAndPosition, _zoomAndPosition2, _zoomAndPosition3;

  const {
    currentActionTreeHierarchy,
    currentActionSubTree
  } = actionEditorStore;
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  useEffect(() => {
    return () => actionEditorStore.clearClickActions();
  }, []);
  if (!currentActionTreeHierarchy || !sharedStore.actionTreesInitialized) return null;

  const onElementsConnect = edge => {
    actionEditorStore.clearClickActions();
    const fromIndex = parseInt(edge.sourceHandle.substring(1));
    const toIndex = parseInt(edge.targetHandle.substring(1));
    const from = currentActionSubTree.getChildActionById(edge.source);
    let to = currentActionSubTree.getChildActionById(edge.target);

    if (to instanceof ActionTreeModel) {
      to = to.enterActions[toIndex];
    }

    if (!from || !to) return;
    groupUndoableActionEditorChanges(ActionEditorChangeGroup.CreateEdge, () => {
      from === null || from === void 0 ? void 0 : from.exits()[fromIndex].addNextAction(to.$modelId);
    });
  };

  const onLoad = reactFlowInstance => {
    setReactFlowInstance(reactFlowInstance);
    undoRedoUpdateReactFlowInstance(reactFlowInstance);
  };

  const onDragOver = event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const createNode = (event, actionModelId, treeModelId) => {
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const {
      x,
      y
    } = reactFlowInstance.project({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top
    });
    const template = sharedStore.actionTreeTemplates.find(t => t.$modelId === treeModelId);
    const source = template ? template : actionModelPrototypes.find(p => p.$modelId === actionModelId);

    if (source instanceof ActionTreeModel) {
      const {
        copy: newAction,
        allNewTrees
      } = source.cloneWithNewIds(ActionTreeType.SubTree);
      configureNewActionInstance(actionEditorStore.currentActionTreeHierarchy, newAction, x, y);
      const tree = lastElement(actionEditorStore.currentActionTreeHierarchy);
      undoableActionEditorDeselectAction();
      newAction.setParentModelId(tree.$modelId);
      undoableActionEditorCreateSubtrees(allNewTrees);
    } else {
      const newAction = clone(source);
      configureNewActionInstance(actionEditorStore.currentActionTreeHierarchy, newAction, x, y);
      const tree = lastElement(actionEditorStore.currentActionTreeHierarchy);
      undoableActionEditorDeselectAction();
      groupUndoableActionEditorChanges(ActionEditorChangeGroup.CreateActionNode, () => {
        tree.addNonSubtreeAction(newAction);
        alignEntriesOnLeft(newAction, tree);
        alignExitsOnRight(newAction, tree);
      });
    }
  };

  const onDrop = event => {
    event.preventDefault();
    const actionModelId = event.dataTransfer.getData('application/newAction');
    const treeModelId = event.dataTransfer.getData('application/newTemplateInstance');
    createNode(event, actionModelId, treeModelId);
  };

  const onClick = event => {
    if (event.target.className !== "react-flow__pane") return;
    const {
      clickPlacementActionModelId: clickPlacementActionNodeModelId,
      clickPlacementActionTreeModelId
    } = actionEditorStore;
    if (!clickPlacementActionNodeModelId && !clickPlacementActionTreeModelId) return;
    createNode(event, clickPlacementActionNodeModelId, clickPlacementActionTreeModelId);
    actionEditorStore.clearClickActions();
  };

  const moveNode = node => {
    if (!node) {
      console.error("moveNode: node was not set: ", node);
      return;
    }

    const action = currentActionSubTree.getChildActionById(node.id);
    const treeHierarchy = actionEditorStore.currentActionTreeHierarchy;
    const {
      x,
      y
    } = onScreenActionPositions(action.position, treeHierarchy);
    const scale = hierarchyScale(treeHierarchy);
    const deltaX = reverseScaleNumber(node.position.x - x, scale);
    const deltaY = reverseScaleNumber(node.position.y - y, scale); // snap into sub tree area or increase/decrease area by moving entry and exit points

    const tree = lastElement(treeHierarchy);
    const newX = action.position.x + deltaX;
    const newY = action.position.y + deltaY;
    setActionPositionAligned(action, tree, newX, newY);
    alignEntriesOnLeft(action, tree);
    alignExitsOnRight(action, tree);
  };

  const onNodeDragStop = (_, node) => {
    groupUndoableActionEditorChanges(ActionEditorChangeGroup.MoveActionNode, () => {
      moveNode(node);
    });
  };

  const onNodeGroupDragStop = (_, nodes) => {
    const nodesByChangedActionTree = new Map();

    for (const node of nodes) {
      const action = currentActionSubTree.getChildActionById(node.id);
      const treeContext = action instanceof ActionTreeModel ? action : getTreeParent(action);
      let groupedNodes = nodesByChangedActionTree.get(treeContext);

      if (!groupedNodes) {
        groupedNodes = new Array();
        nodesByChangedActionTree.set(treeContext, groupedNodes);
      }

      groupedNodes.push(node);
    }

    runInAction(() => {
      for (const groupedNodes of nodesByChangedActionTree.values()) {
        groupUndoableActionEditorChanges(ActionEditorChangeGroup.MoveActionNode, () => {
          groupedNodes.forEach(node => moveNode(node));
        });
      }
    });
  };

  const selectAction = (_, node) => {
    const action = currentActionSubTree === null || currentActionSubTree === void 0 ? void 0 : currentActionSubTree.getChildActionById(node.id);

    if (action) {
      undoableActionEditorSelectAction(action);
    }
  };

  let firstMoveOnRerender = true;

  const onMove = () => {
    var _reactFlowWrapper$cur;

    // NOTE tw: onMove is always called immediately after rerendering. There have been several instances of the ActionEditor
    // freezing in the past, and in one of these instances I observed a rerendering and onMove being constantly triggered. I
    // cannot be *sure* that onMove itself was causing the rerendering but it seems likely, so let's skip this automatic (and
    // probably unnecessary) onMove call for now.
    if (firstMoveOnRerender) {
      firstMoveOnRerender = false;
      return;
    }

    globalActionEditorEvents.emit("onMove");
    if (!reactFlowInstance) return;
    const reactFlowState = reactFlowInstance.toObject();
    const reactFlowBounds = (_reactFlowWrapper$cur = reactFlowWrapper.current) === null || _reactFlowWrapper$cur === void 0 ? void 0 : _reactFlowWrapper$cur.getBoundingClientRect();
    if (!reactFlowBounds) return;
    const currentTreeHierarchy = actionSubTreeFocus.focusedActionSubTreeHierarchy(actionEditorStore.currentRootActionTree, reactFlowState.zoom, reactFlowState.position[0], reactFlowState.position[1], reactFlowBounds.width, reactFlowBounds.height);
    actionEditorStore.setSelectedActionTreeHierarchy(currentTreeHierarchy);
  };

  let nodesAndEdge = [actionAsNode(actionEditorStore.currentRootActionTree, [])];

  for (let i = 0; i < currentActionTreeHierarchy.length; i++) {
    const tree = currentActionTreeHierarchy[i];
    const hierarchy = currentActionTreeHierarchy.slice(0, i + 1);
    const actions = tree.allActions.map(a => actionAsNode(a, hierarchy));
    nodesAndEdge = nodesAndEdge.concat(actions);
    const edges = tree.allActions.map(from => from.exits().map((exit, index) => exit.nextActions.map(to => exitAsEdge(from, index, getEdgeTarget(from, to), hierarchy)))).flat(2).filter(edge => edge != null);
    nodesAndEdge = nodesAndEdge.concat(edges);
  }

  let zoomAndPosition = actionSubTreeFocus.getZoomAndPosition(actionEditorStore.currentRootActionTree);

  if (!zoomAndPosition) {
    const position = actionEditorStore.currentRootActionTree.position;
    const initZoom = 5;
    zoomAndPosition = {
      zoom: 1 / actionEditorStore.actionEditorUpscaleFactor * initZoom,
      x: 100 - position.x * initZoom,
      y: 100 - position.y * initZoom,
      canvasWidth: null,
      canvasHeight: null
    };
  }

  if (reactFlowInstance) {
    reactFlowInstance.setTransform(zoomAndPosition);
  }

  return /*#__PURE__*/React.createElement(FullscreenContainer, {
    ref: reactFlowWrapper
  }, /*#__PURE__*/React.createElement(StyledReactFlow, {
    as: ReactFlow,
    elements: nodesAndEdge,
    onConnect: onElementsConnect,
    onLoad: onLoad,
    onDrop: onDrop,
    onDragOver: onDragOver,
    onNodeDragStart: selectAction,
    onElementClick: selectAction,
    onNodeDragStop: onNodeDragStop,
    onSelectionDragStop: onNodeGroupDragStop,
    onMove: onMove,
    onClick: onClick,
    maxZoom: 10,
    minZoom: 4 / actionEditorStore.actionEditorUpscaleFactor,
    defaultZoom: (_zoomAndPosition = zoomAndPosition) === null || _zoomAndPosition === void 0 ? void 0 : _zoomAndPosition.zoom,
    defaultPosition: [(_zoomAndPosition2 = zoomAndPosition) === null || _zoomAndPosition2 === void 0 ? void 0 : _zoomAndPosition2.x, (_zoomAndPosition3 = zoomAndPosition) === null || _zoomAndPosition3 === void 0 ? void 0 : _zoomAndPosition3.y],
    onlyRenderVisibleElements: true,
    connectionLineComponent: ActionConnectionLine,
    nodeTypes: {
      ActionNode: ActionNode
    },
    edgeTypes: {
      ActionEdge: ActionEdge
    }
  }, /*#__PURE__*/React.createElement(Controls, {
    showInteractive: false
  }), /*#__PURE__*/React.createElement(UndoRedoSlideMenu, null), localSettingsStore.editorComplexity === EditorComplexity.Production && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "action-tree",
    orientation: Adjustment.Left,
    start: 40,
    icon: /*#__PURE__*/React.createElement(CgListTree, null),
    state: State.Expanded,
    collapsible: true
  }, /*#__PURE__*/React.createElement(ActionTreeInfo, null)), /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "action-search",
    orientation: Adjustment.Left,
    start: 130,
    state: State.Collapsed,
    collapsible: true,
    icon: /*#__PURE__*/React.createElement(AiOutlineSearch, null)
  }, /*#__PURE__*/React.createElement(ActionSearchMenu, {
    property: true
  }))), /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "action-navigator",
    orientation: Adjustment.Bottom,
    start: 50,
    maxHeight: navigatorHeight,
    icon: /*#__PURE__*/React.createElement(AiOutlineInteraction, null),
    state: State.Expanded,
    collapsible: true
  }, /*#__PURE__*/React.createElement(ActionNavigator, null)), actionEditorStore.currentAction && /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "action-details",
    orientation: Adjustment.Right,
    start: actionDetailsTop,
    maxHeight: `calc(100vh - ${actionDetailsTop}px - ${navigatorHeight}px)`,
    icon: /*#__PURE__*/React.createElement(GrInspect, null),
    state: State.Expanded,
    collapsible: true,
    containerOverflow: "visible",
    ignorePersistedExpansionState: true,
    notAnimated: true,
    onUserToggle: undoableActionEditorDeselectAction
  }, /*#__PURE__*/React.createElement(ActionDetails, null))));
});